import { DialogActions, DialogContent, DialogTitle, MenuItem, TextField } from '@mui/material';
import { AnimatePresence } from 'framer-motion';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Button } from '@/components/Button';
import { useAlertsStore } from '@/configs/store';
import { useLocalStore } from '@/hooks/useLocalStore';
import { UserSelect } from '@/tracker/entities/user';
import { Board } from '@/tracker/types';
import { ConfirmDialog } from '@/tracker/ui/ConfirmDialog';
import { PresenceDialog } from '@/tracker/ui/PresenceDialog';

import { BoardColumnsEditor } from '../BoardColumnsEditor';
import { BoardSettingsStore } from '../model/BoardSettingsStore';
import { BoardSettingsFormValues, NO_ESTIMATE } from '../types';

import { toFormValues } from './toFormValues';

import s from './BoardSettingsDialog.module.scss';

const ESTIMATE_OPTIONS: { value: NonNullable<Board['estimateBy']>; label: string }[] = [
  { value: 'story_points', label: 'Story points' },
  { value: 'estimation', label: 'Оценка времени' },
];

type Props = {
  /** null — создание новой доски */
  board: Board | null;
  onSaved: (board: Board) => void;
  onDeleted?: () => void;
  onClose: () => void;
};

/** Создание и настройка доски: название, оценка, владелец и колонки со статусами */
const BoardSettingsDialog: React.FC<Props> = observer(({ board, onSaved, onDeleted, onClose }) => {
  const alertsStore = useAlertsStore();
  const { store } = useLocalStore(() => new BoardSettingsStore({ alertsStore }));
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<BoardSettingsFormValues>({ defaultValues: toFormValues(board) });
  const submit = handleSubmit(async ({ name, estimateBy, owner, columns: formColumns }) => {
    const common = {
      name: name.trim(),
      estimateBy: estimateBy || null,
      columns: formColumns.map((column) => ({
        name: column.name.trim(),
        statuses: column.statuses,
      })),
    };

    if (!board) {
      const created = await store.create(common);

      if (created) onSaved(created);

      return;
    }

    const updated = await store.update({
      id: board.id,
      data: {
        ...common,
        columns: formColumns.map((column, index) => ({
          ...common.columns[index],
          ...(column.columnId ? { id: column.columnId } : {}),
        })),
        // Владельца шлём, только если сменили: прежний мог стать неактивным
        ...(owner && owner.id !== board.owner?.id ? { owner: owner.id } : {}),
      },
    });

    if (updated) onSaved(updated);
  });

  const handleDelete = async () => {
    if (board && (await store.remove(board.id))) onDeleted?.();
  };

  return (
    <PresenceDialog onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={submit}>
        <DialogTitle>{board ? 'Настройки доски' : 'Новая доска'}</DialogTitle>
        <DialogContent className={s.content}>
          <TextField
            label="Название"
            size="small"
            autoFocus
            error={!!errors.name}
            helperText={errors.name?.message}
            {...register('name', { validate: (value) => !!value.trim() || 'Введите название' })}
          />
          <div className={s.content__row}>
            <Controller
              control={control}
              name="estimateBy"
              render={({ field }) => (
                <TextField
                  select
                  label="Оценка задач"
                  size="small"
                  fullWidth
                  slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
                  {...field}
                >
                  <MenuItem value={NO_ESTIMATE}>Без оценки</MenuItem>
                  {ESTIMATE_OPTIONS.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            {board && (
              <Controller
                control={control}
                name="owner"
                render={({ field }) => (
                  <UserSelect
                    value={field.value}
                    onChange={field.onChange}
                    ariaLabel="Владелец"
                    placeholder="Владелец"
                    sx={{ width: '100%' }}
                  />
                )}
              />
            )}
          </div>

          <BoardColumnsEditor control={control} register={register} errors={errors} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {board && (
            <Button
              variant="text"
              sx={{ color: 'var(--danger-color)', mr: 'auto' }}
              onClick={() => setIsConfirmingDelete(true)}
            >
              Удалить доску
            </Button>
          )}
          <Button variant="text" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" disabled={store.isSaving}>
            {board ? 'Сохранить' : 'Создать'}
          </Button>
        </DialogActions>
      </form>

      <AnimatePresence>
        {board && isConfirmingDelete && (
          <ConfirmDialog
            title={`Удалить «${board.name}»?`}
            text="Доска и её спринты пропадут. Задачи останутся в своих очередях."
            disabled={store.isSaving}
            onConfirm={handleDelete}
            onClose={() => setIsConfirmingDelete(false)}
          />
        )}
      </AnimatePresence>
    </PresenceDialog>
  );
});

export { BoardSettingsDialog };
